import { Children, Fragment, cloneElement, isValidElement, type CSSProperties, type ReactNode } from "react";
import { EmailNode } from "@react-email/editor/core";
import { Blockquote, Divider } from "@react-email/editor/extensions";
import { EmailTheming, extendTheme } from "@react-email/editor/plugins";
import { SpacedButton } from "./spacedButton";

/**
 * One spacing rule for every block in composed mail: nothing above, a ~1-line
 * gap below, so any two neighbours are exactly BLOCK_GAP apart. Headings are
 * the deliberate exception: more room above, less below, so each one sits
 * with the text it introduces.
 *
 * Margins (in px), not padding: desktop Outlook (Word engine) ignores padding
 * on <p>/<ul>/<h*>/<li> but honours margin. Exceptions that render as tables
 * (buttons) get td padding instead; see spacedButton.ts.
 *
 * The "basic" theme also paints body and container #ffffff, a white card on
 * tinted reading panes (Gmail). Personal mail sits on the client's own
 * background, so both are transparent.
 */
export const BLOCK_GAP = "14px";
const BELOW = `0 0 ${BLOCK_GAP}`;
const HEADING = { paddingTop: "0", margin: "24px 0 8px" };

export const EMAIL_THEME = extendTheme("basic", {
    body: { backgroundColor: "transparent" },
    container: { backgroundColor: "transparent" },
    paragraph: { paddingTop: "0", paddingBottom: "0", margin: BELOW },
    h1: HEADING,
    h2: HEADING,
    h3: HEADING,
    list: { paddingBottom: "0", margin: BELOW },
    listItem: { paddingTop: "0", paddingBottom: "0" },
    codeBlock: { margin: BELOW },
    image: { margin: BELOW },
});

/**
 * The theme expresses every font size in em (px inputs are converted), so the
 * sent mail took the reader's default size (16px in most clients) while the
 * editor shows 14px. Pin the base on the container node all content sits in;
 * the em sizes cascade from it, so the email matches what was typed.
 */
export const BASE_FONT_SIZE = "14px";

type StyleFn = (node: { type?: string }, depth: number, editor: unknown) => CSSProperties;

export function emailTheming(theme: (typeof EmailTheming.options)["theme"]) {
    const base = EmailTheming.options.serializerPlugin as unknown as { getNodeStyles: StyleFn };
    const getNodeStyles: StyleFn = (node, depth, editor) => {
        const styles = base.getNodeStyles(node, depth, editor);
        return node.type === "container" ? { ...styles, fontSize: BASE_FONT_SIZE } : styles;
    };
    return EmailTheming.configure({
        theme: theme ?? EMAIL_THEME,
        serializerPlugin: { ...base, getNodeStyles } as unknown as typeof EmailTheming.options.serializerPlugin,
    });
}

type AnyNode = Parameters<typeof EmailNode.from>[0];
type RenderProps = { children?: ReactNode; style: CSSProperties } & Record<string, unknown>;
type Render = (props: RenderProps) => ReactNode;

/** Re-wrap a stock node's email renderer, keeping everything else about it. */
function rerender(base: { config: object }, wrap: (render: Render, props: RenderProps) => ReactNode) {
    const render = (base.config as { renderToReactEmail: Render }).renderToReactEmail;
    return EmailNode.from(base as unknown as AnyNode, ((props: RenderProps) => wrap(render, props)) as never);
}

/** Zero the bottom margin of the last rendered child (each comes wrapped in a Fragment). */
function dropLastGap(children: ReactNode): ReactNode[] {
    const kids: ReactNode[] = Children.toArray(children);
    const strip = (el: ReactNode): ReactNode => {
        if (!isValidElement<{ children?: ReactNode; style?: CSSProperties }>(el)) return el;
        if (el.type === Fragment) return cloneElement(el, undefined, Children.toArray(el.props.children).map(strip));
        return cloneElement(el, { style: { ...el.props.style, marginBottom: "0px" } });
    };
    if (kids.length) kids[kids.length - 1] = strip(kids[kids.length - 1]);
    return kids;
}

/**
 * Quote and divider have no theme panel, so extendTheme can't reach them; set
 * the gap in their renderers. Quoted paragraphs carry the paragraph gap too,
 * which left an empty line inside the quote bar, so the last one drops it.
 */
export const SpacedBlockquote = rerender(
    Blockquote.configure({ HTMLAttributes: { class: "node-blockquote" } }),
    (render, props) => render({
        ...props,
        style: { ...props.style, margin: BELOW },
        children: dropLastGap(props.children),
    }),
);

export const SpacedDivider = rerender(Divider, (render, props) =>
    render({ ...props, style: { ...props.style, paddingBottom: "0px", margin: BELOW } }),
);

/** StarterKit options to switch off the stock nodes EMAIL_BLOCK_NODES replace. */
export const STARTER_KIT_OVERRIDES = { Button: false, Blockquote: false, Divider: false } as const;
export const EMAIL_BLOCK_NODES = [SpacedButton, SpacedBlockquote, SpacedDivider];
